/**
 * Utilidades para iconos de país en el pasaporte.
 * Busca imágenes en:
 * 1) src/assets/passport (bundled por webpack) — ej. japan.png, jp.png
 * 2) public/assets/passport (URL pública) — mismo nombre de archivo
 * Si existe, se muestra en lugar del emoji de bandera.
 */

const PUBLIC_BASE = `${process.env.PUBLIC_URL || ""}/assets/passport`;

// Imágenes en src/assets/passport: webpack las incluye y devuelve la URL al hacer require
let bundledIcons = {};
try {
	const ctx = require.context(
		"../../assets/passport",
		false,
		/\.(png|jpe?g|svg|webp)$/i
	);
	ctx.keys().forEach((key) => {
		const filename = key.replace(/^\.\//, "");
		bundledIcons[filename.toLowerCase()] = ctx(key);
	});
} catch (_) {
	// Si la carpeta no existe, require.context puede fallar en algunos entornos
}

/**
 * Convierte el nombre del país a un slug para el nombre de archivo.
 * "Japón" -> "japon", "South Korea" -> "south-korea"
 */
function slug(name) {
	if (!name || typeof name !== "string") return "";
	return name
		.toLowerCase()
		.trim()
		.normalize("NFD")
		.replace(/\u0300/g, "")
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "");
}

// Alias en inglés para que japan.png, south-korea.png, etc. funcionen aunque el nombre en app sea "Japón", "Corea del Sur"
const ENGLISH_SLUG = {
	jp: "japan",
	cn: "china",
	th: "thailand",
	es: "spain",
	fr: "france",
	it: "italy",
	de: "alemania",
	gb: "united-kingdom",
	us: "united-states",
	nl: "netherlands",
	mx: "mexico",
	br: "brazil",
	ar: "argentina",
	in: "india",
	id: "indonesia",
	my: "malaysia",
	au: "australia",
	nz: "new-zealand",
	ca: "canada",
	eg: "egipto",
	cr: "costa-rica",
	be: "belgica",
	kr: "south-korea",
	at: "austria",
	co: "colombia",
	pe: "peru",
	kh: "camboya",
	ke: "kenia",
	se: "sweden",
	ch: "switzerland",
	vn: "vietnam",
	ae: "emirates",
	pt: "portugal",
	gr: "greece",
	tr: "turkey",
	ma: "marrakech",
	jo: "jordan",
	sa: "saudi-arabia",
	sg: "singapore",
	ph: "philippines",
	za: "south-africa",
	tz: "tanzania",
	cu: "cuba",
	is: "iceland",
	no: "norway",
	dk: "denmark",
	pl: "poland",
	hu: "hungary",
	ro: "romania",
	hr: "croatia",
	lk: "sri-lanka",
	ie: "ireland",
	fi: "finland",
	cz: "czechia",
	mu: "mauritius",
	sc: "seychelles",
	qa: "qatar",
	mv: "maldives",
	mg: "madagascar",
	np: "nepal",
	om: "oman",
	do: "dominican-republic",
	mt: "malta",
	sk: "slovakia",
	lt: "lithuania",
	lv: "latvia",
	ee: "estonia",
	lu: "luxembourg",
	jm: "jamaica",
	al: "albania",
	va: "vatican-city",
	mc: "monaco",
	bs: "bahamas",
	si: "slovenia",
	cy: "cyprus",
	ec: "ecuador",
	bo: "bolivia",
	uy: "uruguay",
	ve: "venezuela",
	pf: "french-polynesia",
	tw: "taiwan",
	pr: "puerto-rico",
	la: "laos",
	fo: "faroe-islands",
	sm: "san-marino",
	ru: "rusia",
	ua: "ukraine",
	mm: "myanmar",
	uz: "uzbekistan",
	zw: "zimbabwe",
	bg: "bulgaria",
	kw: "kuwait",
	pa: "panama",
	bw: "botsuana",
	bb: "barbados",
	hk: "hong-kong",
	ws: "samoa",
	gt: "guatemala",
	kz: "kazakhstan",
	na: "namibia",
	ad: "andorra",
	mn: "mongolia",
	fj: "fiji",
	me: "montenegro",
	ba: "bosnia-and-herzegovina",
	ge: "georgia",
	mk: "macedonia",
	sv: "el-salvador",
	ni: "nicaragua",
	am: "armenia",
	aw: "aruba",
	cw: "curacao",
	az: "azerbaijan",
	tt: "trinidad-and-tobago",
	cv: "cape-verde",
};

/**
 * Devuelve las URLs a probar para el icono del país, en orden.
 * Busca en src/assets/passport primero (bundled), luego en public/assets/passport.
 * Nombres probados: slug del nombre (japon.png), código (jp.png), alias inglés (japan.png).
 */
export function getPassportIconUrls(countryCode, countryName) {
	const code = (countryCode || "").toLowerCase();
	const nameSlug = slug(countryName);
	const urls = [];
	const tried = new Set();

	function addUrl(filename) {
		if (!filename) return;
		const key = filename.toLowerCase();
		if (tried.has(key)) return;
		tried.add(key);
		if (bundledIcons[key]) {
			urls.push(bundledIcons[key]);
		} else {
			urls.push(`${PUBLIC_BASE}/${filename}`);
		}
	}

	if (nameSlug) addUrl(`${nameSlug}.png`);
	if (code && code.length === 2) {
		addUrl(`${code}.png`);
		if (ENGLISH_SLUG[code]) addUrl(`${ENGLISH_SLUG[code]}.png`);
	}
	return urls;
}

/**
 * Indica si el país tiene icono propio en src/assets/passport (bundled).
 * Usado para asignar color fijo al sello (ej. Japón siempre rojo).
 */
export function hasPassportIcon(countryCode, countryName) {
	const code = (countryCode || "").toLowerCase();
	const nameSlug = slug(countryName);
	if (nameSlug && bundledIcons[`${nameSlug}.png`]) return true;
	if (code && code.length === 2 && bundledIcons[`${code}.png`]) return true;
	if (code && ENGLISH_SLUG[code] && bundledIcons[`${ENGLISH_SLUG[code]}.png`])
		return true;
	return false;
}

/**
 * Color del sello para países con icono propio. Japón siempre rojo; el resto por código o por defecto.
 */
export const STAMP_COLOR_BY_ICON = {
	jp: "#b71c1c",
	es: "#d6101b",
	fr: "#2166a7",
	it: "#2e7d32",
	gb: "#ec5900",
	mx: "#9c6714",
	br: "#3d7625",
	ar: "#10539e",
	id: "#86460d",
	au: "#f06503",
	nz: "#07577b",
	th: "#875a95",
	ca: "#cf0f14",
	de: "#385b3c",
	eg: "#c28d48",
	cr: "#038486",
	be: "#475a6d",
	cn: "#e23027",
	kr: "#be1115",
	at: "#014fa7",
	co: "#d97f00",
	pe: "#d10f1f",
	kh: "#d80918",
	us: "#003e80",
	ke: "#144912",
	se: "#d50001",
	nl: "#163e67",
	ch: "#58290e",
	vn: "#985005",
	ae: "#343946",
	in: "#095566",
	pt: "#005bad",
	gr: "#a76505",
	tr: "#8e5416",
	ma: "#23543e",
	jo: "#d85801",
	sa: "#9e5201",
	sg: "#146693",
	my: "#b31112",
	ph: "#4d6b09",
	za: "#855020",
	tz: "#80951f",
	cu: "#875100",
	is: "#034877",
	no: "#055c71",
	dk: "#962501",
	pl: "#711307",
	hu: "#bf661a",
	ro: "#70180a",
	hr: "#b34203",
	lk: "#3c5202",
	ie: "#91aa2c",
	fi: "#924200",
	mu: "#34b8a2",
	sc: "#118276",
	qa: "#0d5781",
	mv: "#057a7c",
	mg: "#bd4400",
	np: "#1957a0",
	om: "#a68e59",
	do: "#038ea1",
	mt: "#c7940d",
	sk: "#ce252b",
	lv: "#d85b01",
	ee: "#00447e",
	lt: "#d25a01",
	lu: "#d07002",
	jm: "#056e88",
	al: "#c27900",
	va: "#006f9b",
	mc: "#ce6e00",
	bs: "#127d90",
	si: "#425f33",
	cy: "#bf5611",
	ec: "#b06a00",
	bo: "#0e7d7c",
	uy: "#c0620d",
	ve: "#31592e",
	pf: "#114463",
	tw: "#325a32",
	pr: "#943a05",
	la: "#a65e00",
	fo: "#2374a1",
	sm: "#b54109",
	ru: "#c72104",
	ua: "#c77c01",
	mm: "#b96000",
	uz: "#027789",
	zw: "#c64b01",
	bg: "#b15100",
	kw: "#017190",
	pa: "#b23400",
	bw: "#7c3002",
	bb: "#004d61",
	hk: "#0b6c81",
	ws: "#934005",
	gt: "#983900",
	kz: "#8e3904",
	na: "#bd540b",
	ad: "#17523c",
	mn: "#204e32",
	fj: "#4b9985",
	me: "#124364",
	ba: "#672d02",
	ge: "#603510",
	mk: "#6c3002",
	sv: "#851d00",
	ni: "#216294",
	am: "#9c502d",
	aw: "#f05d5c",
	cw: "#7c3a02",
	az: "#4d7498",
	tt: "#fc543a",
	cv: "#255264",
};
const DEFAULT_ICON_COLOR = "#5c6bc0";

export function getStampColorForIcon(countryCode) {
	const code = (countryCode || "").toLowerCase();
	return STAMP_COLOR_BY_ICON[code] || DEFAULT_ICON_COLOR;
}

/**
 * Emoji de bandera a partir del código ISO 3166-1 alpha-2.
 */
export function getFlag(countryCode) {
	if (!countryCode) return "🏳️";
	const code = countryCode.toUpperCase();
	return String.fromCodePoint(
		...code.split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
	);
}
